'use client';

import React, { useState, useMemo } from 'react';
import { Search, Filter, ChevronDown, ChevronUp, ChevronsUpDown, X, Eye, Star, User, Users, CheckCircle, Clock, AlertCircle, FileText } from 'lucide-react';
import { useAppraisalStore } from '@/store/appraisalStore';



// ── Mock submission data ──────────────────────────────────────
interface EmployeeSubmission {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeAvatar: string;
  department: string;
  designation: string;
  cycleId: string;
  cycleName: string;
  submissionStatus: 'not-started' | 'draft-saved' | 'submitted' | 'manager-reviewing' | 'under-review' | 'hr-review' | 'approved' | 'rejected' | 'completed';
  employeeSubmittedAt: string | null;
  managerName: string;
  managerReviewStatus: 'pending' | 'in-progress' | 'completed' | 'rejected';
  managerReviewedAt: string | null;
  managerRating: number | null;
  hrReviewStatus: 'pending' | 'in-progress' | 'completed';
  hrReviewedAt: string | null;
  hrFinalRating: number | null;
  overallScore: number | null;
}

const MOCK_SUBMISSIONS: EmployeeSubmission[] = [
  { id: 'sub-001', employeeId: 'emp-001', employeeName: 'Arjun Kapoor', employeeAvatar: 'AK', department: 'Engineering', designation: 'Senior Engineer', cycleId: 'cycle-001', cycleName: 'Annual Performance Review 2025', submissionStatus: 'completed', employeeSubmittedAt: '2025-04-28T10:30:00Z', managerName: 'Rahul Mehta', managerReviewStatus: 'completed', managerReviewedAt: '2025-05-10T14:00:00Z', managerRating: 4, hrReviewStatus: 'completed', hrReviewedAt: '2025-05-20T11:00:00Z', hrFinalRating: 4, overallScore: 82 },
  { id: 'sub-002', employeeId: 'emp-002', employeeName: 'Sneha Reddy', employeeAvatar: 'SR', department: 'Engineering', designation: 'Software Engineer', cycleId: 'cycle-001', cycleName: 'Annual Performance Review 2025', submissionStatus: 'hr-review', employeeSubmittedAt: '2025-04-25T09:00:00Z', managerName: 'Rahul Mehta', managerReviewStatus: 'completed', managerReviewedAt: '2025-05-08T16:00:00Z', managerRating: 5, hrReviewStatus: 'in-progress', hrReviewedAt: null, hrFinalRating: null, overallScore: null },
  { id: 'sub-003', employeeId: 'emp-003', employeeName: 'Kiran Nair', employeeAvatar: 'KN', department: 'Product', designation: 'Product Manager', cycleId: 'cycle-001', cycleName: 'Annual Performance Review 2025', submissionStatus: 'manager-reviewing', employeeSubmittedAt: '2025-04-30T11:00:00Z', managerName: 'Vikram Iyer', managerReviewStatus: 'in-progress', managerReviewedAt: null, managerRating: null, hrReviewStatus: 'pending', hrReviewedAt: null, hrFinalRating: null, overallScore: null },
  { id: 'sub-004', employeeId: 'emp-004', employeeName: 'Divya Sharma', employeeAvatar: 'DS', department: 'Sales', designation: 'Sales Executive', cycleId: 'cycle-001', cycleName: 'Annual Performance Review 2025', submissionStatus: 'submitted', employeeSubmittedAt: '2025-04-29T08:30:00Z', managerName: 'Ananya Sharma', managerReviewStatus: 'pending', managerReviewedAt: null, managerRating: null, hrReviewStatus: 'pending', hrReviewedAt: null, hrFinalRating: null, overallScore: null },
  { id: 'sub-005', employeeId: 'emp-005', employeeName: 'Rohan Gupta', employeeAvatar: 'RG', department: 'Engineering', designation: 'Tech Lead', cycleId: 'cycle-001', cycleName: 'Annual Performance Review 2025', submissionStatus: 'draft-saved', employeeSubmittedAt: null, managerName: 'Rahul Mehta', managerReviewStatus: 'pending', managerReviewedAt: null, managerRating: null, hrReviewStatus: 'pending', hrReviewedAt: null, hrFinalRating: null, overallScore: null },
  { id: 'sub-006', employeeId: 'emp-006', employeeName: 'Meera Pillai', employeeAvatar: 'MP', department: 'Customer Support', designation: 'Support Lead', cycleId: 'cycle-001', cycleName: 'Annual Performance Review 2025', submissionStatus: 'not-started', employeeSubmittedAt: null, managerName: 'Ananya Sharma', managerReviewStatus: 'pending', managerReviewedAt: null, managerRating: null, hrReviewStatus: 'pending', hrReviewedAt: null, hrFinalRating: null, overallScore: null },
  { id: 'sub-007', employeeId: 'emp-007', employeeName: 'Aditya Singh', employeeAvatar: 'AS', department: 'Finance', designation: 'Finance Analyst', cycleId: 'cycle-002', cycleName: 'Mid-Year Review Q2 2025', submissionStatus: 'completed', employeeSubmittedAt: '2025-04-10T10:00:00Z', managerName: 'Vikram Iyer', managerReviewStatus: 'completed', managerReviewedAt: '2025-04-25T09:00:00Z', managerRating: 3, hrReviewStatus: 'completed', hrReviewedAt: '2025-05-05T14:00:00Z', hrFinalRating: 3, overallScore: 68 },
  { id: 'sub-008', employeeId: 'emp-008', employeeName: 'Priya Krishnan', employeeAvatar: 'PK', department: 'Marketing', designation: 'Marketing Manager', cycleId: 'cycle-002', cycleName: 'Mid-Year Review Q2 2025', submissionStatus: 'approved', employeeSubmittedAt: '2025-04-12T11:00:00Z', managerName: 'Vikram Iyer', managerReviewStatus: 'completed', managerReviewedAt: '2025-04-28T10:00:00Z', managerRating: 5, hrReviewStatus: 'completed', hrReviewedAt: '2025-05-08T15:00:00Z', hrFinalRating: 5, overallScore: 94 },
  { id: 'sub-009', employeeId: 'emp-009', employeeName: 'Suresh Babu', employeeAvatar: 'SB', department: 'Operations', designation: 'Operations Manager', cycleId: 'cycle-002', cycleName: 'Mid-Year Review Q2 2025', submissionStatus: 'rejected', employeeSubmittedAt: '2025-04-15T09:00:00Z', managerName: 'Rahul Mehta', managerReviewStatus: 'rejected', managerReviewedAt: '2025-04-30T11:00:00Z', managerRating: 2, hrReviewStatus: 'pending', hrReviewedAt: null, hrFinalRating: null, overallScore: null },
  { id: 'sub-010', employeeId: 'emp-010', employeeName: 'Lakshmi Rao', employeeAvatar: 'LR', department: 'HR', designation: 'HR Executive', cycleId: 'cycle-001', cycleName: 'Annual Performance Review 2025', submissionStatus: 'under-review', employeeSubmittedAt: '2025-04-27T14:00:00Z', managerName: 'Priya Nair', managerReviewStatus: 'completed', managerReviewedAt: '2025-05-12T10:00:00Z', managerRating: 4, hrReviewStatus: 'in-progress', hrReviewedAt: null, hrFinalRating: null, overallScore: null },
];

const SUBMISSION_STATUS_CONFIG: Record<string, { label: string; class: string }> = {
  'not-started': { label: 'Not Started', class: 'pn-appraisal__status-badge--not-started' },
  'draft-saved': { label: 'Draft Saved', class: 'pn-appraisal__status-badge--draft' },
  'submitted': { label: 'Submitted', class: 'pn-appraisal__status-badge--published' },
  'manager-reviewing': { label: 'Manager Reviewing', class: 'pn-appraisal__status-badge--in-progress' },
  'under-review': { label: 'Under Review', class: 'pn-appraisal__status-badge--under-review' },
  'hr-review': { label: 'HR Review', class: 'pn-appraisal__status-badge--hr-review' },
  'approved': { label: 'Approved', class: 'pn-appraisal__status-badge--completed' },
  'rejected': { label: 'Rejected', class: 'pn-appraisal__status-badge--overdue' },
  'completed': { label: 'Completed', class: 'pn-appraisal__status-badge--completed' },
};

const REVIEW_STATUS_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  'pending': { label: 'Pending', icon: <Clock size={12} />, color: 'var(--pn-text-muted)' },
  'in-progress': { label: 'In Progress', icon: <AlertCircle size={12} />, color: 'var(--pn-warning)' },
  'completed': { label: 'Completed', icon: <CheckCircle size={12} />, color: 'var(--pn-success)' },
  'rejected': { label: 'Rejected', icon: <X size={12} />, color: 'var(--pn-danger)' },
};

type SortKey = 'employeeName' | 'department' | 'cycleName' | 'submissionStatus' | 'managerReviewStatus' | 'overallScore';
type SortDir = 'asc' | 'desc';

function StarRating({ rating, max = 5 }: { rating: number | null; max?: number }) {
  if (rating === null) return <span style={{ color: 'var(--pn-text-muted)', fontSize: 12 }}>—</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      {Array.from({ length: max }, (_, i) => (
        <Star
          key={i}
          size={12}
          fill={i < rating ? 'var(--pn-warning)' : 'none'}
          color={i < rating ? 'var(--pn-warning)' : 'var(--pn-border-strong)'}
        />
      ))}
      <span style={{ fontSize: 11, color: 'var(--pn-text-secondary)', marginLeft: 2 }}>{rating}/{max}</span>
    </div>
  );
}

function DetailModal({ submission, onClose }: { submission: EmployeeSubmission; onClose: () => void }) {
  const statusCfg = SUBMISSION_STATUS_CONFIG[submission.submissionStatus];
  return (
    <div className="pn-appraisal__modal-overlay" role="dialog" aria-modal="true">
      <div className="pn-appraisal__modal pn-appraisal__modal--lg">
        <div className="pn-appraisal__modal-header">
          <div>
            <div className="pn-appraisal__modal-header-title">Submission Details</div>
            <div className="pn-appraisal__modal-header-subtitle">{submission.cycleName}</div>
          </div>
          <button className="pn-appraisal__modal-header-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="pn-appraisal__modal-body">
          {/* Employee Info */}
          <div className="pn-appraisal__submissions-detail-employee">
            <div className="pn-appraisal__submissions-detail-avatar">{submission.employeeAvatar}</div>
            <div className="pn-appraisal__submissions-detail-info">
              <div className="pn-appraisal__submissions-detail-name">{submission.employeeName}</div>
              <div className="pn-appraisal__submissions-detail-meta">{submission.designation} · {submission.department}</div>
            </div>
            <span className={`pn-appraisal__status-badge ${statusCfg.class}`}>{statusCfg.label}</span>
          </div>

          {/* Timeline */}
          <div className="pn-appraisal__submissions-timeline">
            <div className="pn-appraisal__submissions-timeline-title">Review Timeline</div>
            {[
              {
                role: 'Employee Submission',
                icon: <User size={14} />,
                status: submission.employeeSubmittedAt ? 'completed' : submission.submissionStatus === 'draft-saved' ? 'in-progress' : 'pending',
                date: submission.employeeSubmittedAt,
                rating: null,
                person: submission.employeeName,
              },
              {
                role: 'Manager Review',
                icon: <Users size={14} />,
                status: submission.managerReviewStatus,
                date: submission.managerReviewedAt,
                rating: submission.managerRating,
                person: submission.managerName,
              },
              {
                role: 'HR Final Review',
                icon: <FileText size={14} />,
                status: submission.hrReviewStatus,
                date: submission.hrReviewedAt,
                rating: submission.hrFinalRating,
                person: 'HR Team',
              },
            ].map((stage, idx) => {
              const cfg = REVIEW_STATUS_CONFIG[stage.status] || REVIEW_STATUS_CONFIG['pending'];
              return (
                <div key={idx} className="pn-appraisal__submissions-timeline-item">
                  <div className="pn-appraisal__submissions-timeline-icon" style={{ color: cfg.color, borderColor: cfg.color, background: stage.status === 'completed' ? 'var(--pn-success-light)' : 'var(--pn-surface-2)' }}>
                    {stage.icon}
                  </div>
                  <div className="pn-appraisal__submissions-timeline-content">
                    <div className="pn-appraisal__submissions-timeline-role">{stage.role}</div>
                    <div className="pn-appraisal__submissions-timeline-person">{stage.person}</div>
                    {stage.date && (
                      <div className="pn-appraisal__submissions-timeline-date">
                        {new Date(stage.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                    {stage.rating !== null && (
                      <div style={{ marginTop: 4 }}>
                        <StarRating rating={stage.rating} />
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: cfg.color, fontWeight: 600 }}>
                    {cfg.icon} {cfg.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Score Summary */}
          {submission.overallScore !== null && (
            <div className="pn-appraisal__submissions-score-card">
              <div className="pn-appraisal__submissions-score-label">Overall Score</div>
              <div className="pn-appraisal__submissions-score-value">{submission.overallScore}%</div>
              <div className="pn-appraisal__submissions-score-bar">
                <div
                  className="pn-appraisal__submissions-score-bar-fill"
                  style={{
                    width: `${submission.overallScore}%`,
                    background: submission.overallScore >= 80 ? 'var(--pn-success)' : submission.overallScore >= 60 ? 'var(--pn-primary)' : submission.overallScore >= 40 ? 'var(--pn-warning)' : 'var(--pn-danger)',
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <div className="pn-appraisal__modal-footer pn-appraisal__modal-footer--end">
          <button className="pn-appraisal__btn pn-appraisal__btn--secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function SubmissionsClient() {
  const { cycles, setCycles } = useAppraisalStore();
  const [search, setSearch] = useState('');
  const [cycleFilter, setCycleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('employeeName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);
  const [selectedSubmission, setSelectedSubmission] = useState<EmployeeSubmission | null>(null);
  const perPage = 8;

  const departments = Array.from(new Set(MOCK_SUBMISSIONS.map((s) => s.department))).sort();
  const cycleNames = Array.from(new Set(MOCK_SUBMISSIONS.map((s) => ({ id: s.cycleId, name: s.cycleName }))).values());
  const uniqueCycles = MOCK_SUBMISSIONS.reduce<{ id: string; name: string }[]>((acc, s) => {
    if (!acc.find((c) => c.id === s.cycleId)) acc.push({ id: s.cycleId, name: s.cycleName });
    return acc;
  }, []);

  const filtered = useMemo(() => {
    let result = [...MOCK_SUBMISSIONS];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((s) =>
        s.employeeName.toLowerCase().includes(q) ||
        s.department.toLowerCase().includes(q) ||
        s.managerName.toLowerCase().includes(q) ||
        s.designation.toLowerCase().includes(q)
      );
    }
    if (cycleFilter !== 'all') result = result.filter((s) => s.cycleId === cycleFilter);
    if (statusFilter !== 'all') result = result.filter((s) => s.submissionStatus === statusFilter);
    if (deptFilter !== 'all') result = result.filter((s) => s.department === deptFilter);

    result.sort((a, b) => {
      let va: any = a[sortKey];
      let vb: any = b[sortKey];
      if (va === null) va = '';
      if (vb === null) vb = '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [search, cycleFilter, statusFilter, deptFilter, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={12} style={{ opacity: 0.4 }} />;
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  // KPI counts
  const totalSubmissions = MOCK_SUBMISSIONS.length;
  const submitted = MOCK_SUBMISSIONS.filter((s) => s.employeeSubmittedAt).length;
  const managerPending = MOCK_SUBMISSIONS.filter((s) => s.managerReviewStatus === 'pending' || s.managerReviewStatus === 'in-progress').length;
  const hrPending = MOCK_SUBMISSIONS.filter((s) => s.hrReviewStatus === 'pending' || s.hrReviewStatus === 'in-progress').length;
  const completed = MOCK_SUBMISSIONS.filter((s) => s.submissionStatus === 'completed').length;

  return (
    <>
      {/* Page Header */}
      <div className="pn-appraisal__page-header">
        <div className="pn-appraisal__page-header-left">
          <h1 className="pn-appraisal__page-header-title">Submissions & Reviews</h1>
          <p className="pn-appraisal__page-header-subtitle">
            Monitor employee and manager appraisal submissions across all cycles
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="pn-appraisal__submissions-kpis">
        {[
          { label: 'Total Assigned', value: totalSubmissions, icon: <Users size={18} />, color: 'var(--pn-primary)', bg: 'var(--pn-primary-light)' },
          { label: 'Employee Submitted', value: submitted, icon: <FileText size={18} />, color: 'var(--pn-info)', bg: 'var(--pn-info-light)' },
          { label: 'Manager Review Pending', value: managerPending, icon: <Clock size={18} />, color: 'var(--pn-warning)', bg: 'var(--pn-warning-light)' },
          { label: 'HR Review Pending', value: hrPending, icon: <AlertCircle size={18} />, color: 'var(--pn-accent)', bg: 'var(--pn-accent-light)' },
          { label: 'Completed', value: completed, icon: <CheckCircle size={18} />, color: 'var(--pn-success)', bg: 'var(--pn-success-light)' },
        ].map((kpi) => (
          <div key={kpi.label} className="pn-appraisal__submissions-kpi-card">
            <div className="pn-appraisal__submissions-kpi-icon" style={{ color: kpi.color, background: kpi.bg }}>
              {kpi.icon}
            </div>
            <div className="pn-appraisal__submissions-kpi-content">
              <div className="pn-appraisal__submissions-kpi-value">{kpi.value}</div>
              <div className="pn-appraisal__submissions-kpi-label">{kpi.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="pn-appraisal__filter-bar" style={{ flexWrap: 'wrap' }}>
        <div className="pn-appraisal__filter-bar-search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search employees, managers, departments…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            aria-label="Search submissions"
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <X size={12} color="var(--pn-text-muted)" />
            </button>
          )}
        </div>

        <div className="pn-appraisal__filter-bar-divider" />

        <select
          className="pn-appraisal__filter-bar-select"
          value={cycleFilter}
          onChange={(e) => { setCycleFilter(e.target.value); setPage(1); }}
          aria-label="Filter by cycle"
        >
          <option value="all">All Cycles</option>
          {uniqueCycles.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <select
          className="pn-appraisal__filter-bar-select"
          value={deptFilter}
          onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }}
          aria-label="Filter by department"
        >
          <option value="all">All Departments</option>
          {departments.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        <select
          className="pn-appraisal__filter-bar-select"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          aria-label="Filter by status"
        >
          <option value="all">All Statuses</option>
          {Object.entries(SUBMISSION_STATUS_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="pn-appraisal__table-wrapper">
        <div className="pn-appraisal__table-header">
          <div className="pn-appraisal__table-header-left">
            <span className="pn-appraisal__table-header-title">Submission Records</span>
            <span className="pn-appraisal__table-header-count">{filtered.length} results</span>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="pn-appraisal__table">
            <thead>
              <tr>
                <th className="pn-appraisal__table-th" onClick={() => handleSort('employeeName')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Employee <SortIcon col="employeeName" /></div>
                </th>
                <th className="pn-appraisal__table-th" onClick={() => handleSort('department')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Department <SortIcon col="department" /></div>
                </th>
                <th className="pn-appraisal__table-th" onClick={() => handleSort('cycleName')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Cycle <SortIcon col="cycleName" /></div>
                </th>
                <th className="pn-appraisal__table-th" onClick={() => handleSort('submissionStatus')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Status <SortIcon col="submissionStatus" /></div>
                </th>
                <th className="pn-appraisal__table-th">Employee Submitted</th>
                <th className="pn-appraisal__table-th" onClick={() => handleSort('managerReviewStatus')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Manager Review <SortIcon col="managerReviewStatus" /></div>
                </th>
                <th className="pn-appraisal__table-th">Manager Rating</th>
                <th className="pn-appraisal__table-th">HR Review</th>
                <th className="pn-appraisal__table-th" onClick={() => handleSort('overallScore')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Score <SortIcon col="overallScore" /></div>
                </th>
                <th className="pn-appraisal__table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--pn-text-muted)', fontSize: 14 }}>
                    No submissions found matching your filters.
                  </td>
                </tr>
              ) : (
                paginated.map((sub) => {
                  const statusCfg = SUBMISSION_STATUS_CONFIG[sub.submissionStatus];
                  const mgrCfg = REVIEW_STATUS_CONFIG[sub.managerReviewStatus];
                  const hrCfg = REVIEW_STATUS_CONFIG[sub.hrReviewStatus];
                  return (
                    <tr key={sub.id} className="pn-appraisal__table-row">
                      <td className="pn-appraisal__table-td">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div className="pn-appraisal__submissions-avatar">{sub.employeeAvatar}</div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--pn-text-primary)' }}>{sub.employeeName}</div>
                            <div style={{ fontSize: 11.5, color: 'var(--pn-text-muted)' }}>{sub.designation}</div>
                          </div>
                        </div>
                      </td>
                      <td className="pn-appraisal__table-td">
                        <span style={{ fontSize: 12.5, color: 'var(--pn-text-secondary)' }}>{sub.department}</span>
                      </td>
                      <td className="pn-appraisal__table-td">
                        <span style={{ fontSize: 12, color: 'var(--pn-text-secondary)', maxWidth: 160, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sub.cycleName}>{sub.cycleName}</span>
                      </td>
                      <td className="pn-appraisal__table-td">
                        <span className={`pn-appraisal__status-badge ${statusCfg.class}`}>{statusCfg.label}</span>
                      </td>
                      <td className="pn-appraisal__table-td">
                        {sub.employeeSubmittedAt ? (
                          <div>
                            <div style={{ fontSize: 12, color: 'var(--pn-success)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <CheckCircle size={12} /> Submitted
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--pn-text-muted)' }}>
                              {new Date(sub.employeeSubmittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            </div>
                          </div>
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--pn-text-muted)' }}>—</span>
                        )}
                      </td>
                      <td className="pn-appraisal__table-td">
                        <div>
                          <div style={{ fontSize: 12, color: mgrCfg.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                            {mgrCfg.icon} {mgrCfg.label}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--pn-text-muted)' }}>{sub.managerName}</div>
                        </div>
                      </td>
                      <td className="pn-appraisal__table-td">
                        <StarRating rating={sub.managerRating} />
                      </td>
                      <td className="pn-appraisal__table-td">
                        <div style={{ fontSize: 12, color: hrCfg.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                          {hrCfg.icon} {hrCfg.label}
                        </div>
                      </td>
                      <td className="pn-appraisal__table-td">
                        {sub.overallScore !== null ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{
                              width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontWeight: 800, fontSize: 12,
                              background: sub.overallScore >= 80 ? 'var(--pn-success-light)' : sub.overallScore >= 60 ? 'var(--pn-primary-light)' : 'var(--pn-warning-light)',
                              color: sub.overallScore >= 80 ? 'var(--pn-success)' : sub.overallScore >= 60 ? 'var(--pn-primary)' : 'var(--pn-warning)',
                            }}>
                              {sub.overallScore}
                            </div>
                          </div>
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--pn-text-muted)' }}>—</span>
                        )}
                      </td>
                      <td className="pn-appraisal__table-td">
                        <button
                          className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--sm"
                          onClick={() => setSelectedSubmission(sub)}
                          aria-label={`View details for ${sub.employeeName}`}
                        >
                          <Eye size={13} /> View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pn-appraisal__table-pagination">
            <span className="pn-appraisal__table-pagination-info">
              Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}
            </span>
            <div className="pn-appraisal__table-pagination-controls">
              <button
                className="pn-appraisal__btn pn-appraisal__btn--secondary pn-appraisal__btn--sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={`page-${p}`}
                  className={`pn-appraisal__btn pn-appraisal__btn--sm${page === p ? ' pn-appraisal__btn--primary' : ' pn-appraisal__btn--secondary'}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ))}
              <button
                className="pn-appraisal__btn pn-appraisal__btn--secondary pn-appraisal__btn--sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedSubmission && (
        <DetailModal submission={selectedSubmission} onClose={() => setSelectedSubmission(null)} />
      )}
    </>
  );
}
